/**
 * @deprecated Phase B: Use POST /monitoring/providers/:providerCode/link-user instead.
 * This page will be removed in Phase C.
 */
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  alpha,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@refinedev/core";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
import { CountryPhoneInput } from "../../components/PhoneInput";
import { linkExistingUser, createNewUserWithVigilane } from "../../api/vigilane";

export const VigilaneLink: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { open } = useNotification();

  // État pour le choix du type d'action
  const [actionType, setActionType] = useState<"existing" | "new">("existing");

  // États pour les inputs
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tlsId, setTlsId] = useState("");

  // États pour les erreurs de validation
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [tlsIdError, setTlsIdError] = useState("");

  // États pour les modals
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Regex pour validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const tlsIdRegex = /^\d{6,20}$/; // Chiffres uniquement, 6-20 caractères

  // Fonctions de validation
  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError("L'email est obligatoire");
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError("Format d'email invalide");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePhone = (value: string): boolean => {
    if (!value) {
      setPhoneError("Le numéro de téléphone est obligatoire");
      return false;
    }

    // Validation avec libphonenumber-js
    try {
      if (!isValidPhoneNumber(value)) {
        setPhoneError("Format de téléphone invalide");
        return false;
      }
      setPhoneError("");
      return true;
    } catch (error) {
      setPhoneError("Format de téléphone invalide");
      return false;
    }
  };

  const validateTlsId = (value: string): boolean => {
    if (!value) {
      setTlsIdError("L'identifiant TLS est obligatoire");
      return false;
    }
    if (!tlsIdRegex.test(value)) {
      setTlsIdError("L'identifiant TLS doit contenir 6 à 20 chiffres");
      return false;
    }
    setTlsIdError("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Réinitialiser les erreurs
    setEmailError("");
    setPhoneError("");
    setTlsIdError("");

    // Validation selon le type d'action
    let isValid = true;

    isValid = validateEmail(email) && isValid;
    isValid = validateTlsId(tlsId) && isValid;

    if (actionType === "existing") {
      isValid = validatePhone(phone) && isValid;
    }

    if (!isValid) {
      open?.({
        type: "error",
        message: "Erreur de validation",
        description: "Veuillez corriger les erreurs dans le formulaire",
      });
      return;
    }

    // Ouvrir la modal de confirmation
    setConfirmationOpen(true);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);

    try {
      const vigilaneId = parseInt(tlsId, 10);

      if (actionType === "existing") {
        // Lier un utilisateur existant
        const response = await linkExistingUser({
          email: email.trim(),
          phone: phone.trim(),
          vigilaneId,
        });

        setConfirmationOpen(false);

        open?.({
          type: "success",
          message: "Liaison effectuée avec succès",
          description: response.message || `L'utilisateur ${email} a été lié avec le TLS ID ${tlsId}`,
        });
      } else {
        // Créer un nouvel utilisateur
        const response = await createNewUserWithVigilane({
          email: email.trim(),
          vigilaneId,
        });

        setConfirmationOpen(false);

        const emailWarning = response.data?.emailSent === false
          ? " ATTENTION: L'envoi de l'email a échoué."
          : "";

        open?.({
          type: "success",
          message: "Compte créé avec succès",
          description: (response.message || `Un nouveau compte a été créé pour ${email} avec le TLS ID ${tlsId}`) + emailWarning,
        });
      }

      // Rediriger vers la liste des utilisateurs après un délai
      setTimeout(() => {
        navigate("/users");
      }, 2000);
    } catch (error: any) {
      console.error("Erreur lors de la liaison:", error);

      const errorMessage = error?.response?.data?.message || error?.message || "Une erreur est survenue";

      open?.({
        type: "error",
        message: "Erreur lors de la liaison",
        description: errorMessage,
      });

      setConfirmationOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActionType(event.target.value as "existing" | "new");
    // Réinitialiser uniquement les erreurs, pas les champs
    setEmailError("");
    setPhoneError("");
    setTlsIdError("");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card
        sx={{
          maxWidth: 800,
          mx: "auto",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <CardHeader
          title={
            <Typography variant="h5" fontWeight={600}>
              Lier un utilisateur Vigilane à Safee
            </Typography>
          }
          subheader="Remplissez les informations ci-dessous"
        />
        <CardContent>
          {/* Formulaire */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Radio buttons pour choisir le type d'action */}
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                    Type d'action
                  </FormLabel>
                  <RadioGroup
                    row
                    value={actionType}
                    onChange={handleActionTypeChange}
                    sx={{ gap: 2 }}
                  >
                    <FormControlLabel
                      value="existing"
                      control={<Radio />}
                      label="Compte Safee existant"
                      sx={{
                        flex: 1,
                        border: `2px solid ${actionType === "existing" ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1.5,
                        m: 0,
                        bgcolor: actionType === "existing" ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                        transition: "all 0.2s",
                      }}
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="Nouveau compte Safee"
                      sx={{
                        flex: 1,
                        border: `2px solid ${actionType === "new" ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1.5,
                        m: 0,
                        bgcolor: actionType === "new" ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                        transition: "all 0.2s",
                      }}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Champs selon le type d'action */}
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={(e) => validateEmail(e.target.value)}
                  error={!!emailError}
                  helperText={emailError || "Email de l'utilisateur"}
                  variant="outlined"
                  placeholder="exemple@email.com"
                />

                {actionType === "existing" && (
                  <CountryPhoneInput
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      if (phoneError && value) validatePhone(value);
                    }}
                    onBlur={() => validatePhone(phone)}
                    error={!!phoneError}
                    helperText={phoneError || "Le numéro sera formaté au format international"}
                    required
                    label="Numéro de téléphone"
                    placeholder="6 12 34 56 78"
                  />
                )}

                <TextField
                  fullWidth
                  required
                  label="Identifiant TLS"
                  value={tlsId}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, ''); // Ne garde que les chiffres
                    setTlsId(numericValue);
                    if (tlsIdError) validateTlsId(numericValue);
                  }}
                  onBlur={(e) => validateTlsId(e.target.value)}
                  error={!!tlsIdError}
                  helperText={tlsIdError || "6 à 20 chiffres"}
                  variant="outlined"
                  placeholder="123456789"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      !email ||
                      !tlsId ||
                      (actionType === "existing" && !phone)
                    }
                  >
                    Suivant
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        </CardContent>
      </Card>

      {/* Modal de confirmation */}
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight={600}>
            Confirmer la liaison
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={600}>
                  Type d'action
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  {actionType === "existing"
                    ? "Ajout du TLS_ID à un compte Safee existant"
                    : "Création d'un nouveau compte Safee avec le TLS_ID"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={600}>
                  Email
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  {email}
                </Typography>
              </Box>

              {actionType === "existing" && (
                <Box>
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    Numéro de téléphone
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    {(() => {
                      try {
                        const parsed = parsePhoneNumber(phone);
                        return parsed?.formatInternational() || phone;
                      } catch {
                        return phone;
                      }
                    })()}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={600}>
                  Identifiant TLS
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600, color: theme.palette.primary.main }}>
                  {tlsId}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Veuillez vérifier les informations ci-dessus avant de confirmer.
          </Typography>
        </DialogContent>
        <DialogContent sx={{ pt: 0 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setConfirmationOpen(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {isSubmitting ? "En cours..." : "Confirmer"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
